import axios from 'axios';
import qs from 'qs';
import Raven from 'raven-js';
import { currentToken, currentImpersonatorToken } from '~/common/auth';
import {
  getApiHost,
  getAppSecure,
  logoutWithRedirectBack,
  getAggregatorApiHost,
  getCoeusApiHost,
} from '~/common/utils';

class Naluri {
  static uri(path, naluriAgg, naluriCoeus) {
    const apiEndpoint = naluriAgg
      ? getAggregatorApiHost()
      : naluriCoeus
      ? getCoeusApiHost()
      : getApiHost();
    return getAppSecure('naluri') + apiEndpoint + path;
  }

  static async headers() {
    return currentToken()
      .then(token => {
        let header = {
          'content-type': 'application/json',
          accept: 'application/json',
          'app-version': 'coach-app',
          authorization: 'Bearer ' + token,
        };

        let impersonatorToken = currentImpersonatorToken();
        if (impersonatorToken) {
          header = {
            ...header,
            'x-naluri-impersonator-token': impersonatorToken,
          };
        }

        return header;
      })
      .catch(err => {
        if (err.code === 'NotAuthorizedException') {
          // If refresh token, or current session is expired,
          // Logout and redirect users.
          logoutWithRedirectBack();
        }
      });
  }

  static async request(resources, params, opts = {}, naluriAgg, naluriCoeus) {
    // If the user refresh token is expired, headers will be undefined.
    let headers = opts.headers ? opts.headers : await Naluri.headers();
    let endpoint = Naluri.uri(resources, naluriAgg, naluriCoeus);

    let options = Object.assign(
      {},
      {
        url: endpoint,
        headers,
      },
      opts,
    );

    return axios(endpoint, options)
      .then(Naluri.handleResponse)
      .catch(e => {
        throw Naluri.handleError(e);
      });
  }

  static get(resources, params, opts = {}, naluriAgg = false, naluriCoeus = false) {
    opts = Object.assign({}, opts, {
      method: 'GET',
      data: null,
    });

    resources = !params ? resources : resources + '?' + qs.stringify(params);

    return Naluri.request(resources, null, opts, naluriAgg, naluriCoeus);
  }

  static post(resources, params, opts = {}, naluriAgg = false, naluriCoeus = false) {
    opts = Object.assign({}, opts, {
      data: Naluri.serialize(params),
      method: 'POST',
    });

    return Naluri.request(resources, params, opts, naluriAgg, naluriCoeus);
  }

  static formidable(resource, request, opts = {}) {
    let headers = Naluri.headers();
    delete headers['content-type'];
    delete headers['accept'];

    opts = {
      headers: { ...headers, 'content-type': 'multipart/form-data' },
      method: opts.method,
      data: request,
    };

    return Naluri.request(resource, request, opts);
  }

  static put(resources, params, opts = {}) {
    opts = Object.assign({}, opts, {
      data: Naluri.serialize(params),
      method: 'PUT',
    });

    return Naluri.request(resources, params, opts);
  }

  static delete(resources, params, opts = {}) {
    opts = Object.assign({}, opts, {
      data: Naluri.serialize(params),
      method: 'DELETE',
    });

    return Naluri.request(resources, params, opts);
  }

  static serialize(params) {
    return JSON.stringify(params);
  }

  static handleResponse(response) {
    const { status, data } = response;

    if (status < 200 && status > 300) {
      throw this.handleError(response);
    }
    return data;
  }

  static handleError(error) {
    const errs = error && error.response ? error.response.data : error;
    Raven.captureException(new Error(errs), {
      tags: {
        error_type: 'API Failed',
        error_url: error.request.responseURL + ':' + error.request.status,
      },
    });
    return errs;
  }
}

export default Naluri;



// WEBPACK FOOTER //
// ./src/common/api/naluri.js